import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Mail, Shield, Crown, Trash2, UserPlus } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleIcons = {
  owner: <Crown className="w-4 h-4 text-yellow-500" />,
  admin: <Shield className="w-4 h-4 text-secondary" />,
  member: <Users className="w-4 h-4 text-primary" />
};

export default function TeamsPage() {
  const { getAuthHeaders, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [inviteData, setInviteData] = useState({ email: '', role: 'member' });

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API_URL}/teams`, {
        headers: getAuthHeaders()
      });
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, [getAuthHeaders]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/teams`, newTeam, {
        headers: getAuthHeaders()
      });
      setTeams([...teams, response.data]);
      setCreateDialogOpen(false);
      setNewTeam({ name: '', description: '' });
      toast.success('Team created successfully!');
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!selectedTeam) return;
    
    try {
      await axios.post(`${API_URL}/teams/${selectedTeam.id}/invite`, inviteData, {
        headers: getAuthHeaders()
      });
      toast.success(`Invitation sent to ${inviteData.email}`);
      setInviteDialogOpen(false);
      setInviteData({ email: '', role: 'member' });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await axios.delete(`${API_URL}/teams/${teamId}/members/${userId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Member removed');
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const openInviteDialog = (team) => {
    setSelectedTeam(team);
    setInviteDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="teams-title">
              Teams
            </h1>
            <p className="text-foreground/60">Collaborate with your team on deployments</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-bold hover:shadow-neon-cyan" data-testid="create-team-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Team
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" aria-describedby="create-team-description">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Create New Team</DialogTitle>
                <p id="create-team-description" className="sr-only">Form to create a new team</p>
              </DialogHeader>
              <form onSubmit={handleCreateTeam} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Team Name</Label>
                  <Input
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    placeholder="e.g., Backend Team"
                    className="bg-background border-border"
                    required
                    data-testid="team-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Description</Label>
                  <Input
                    value={newTeam.description}
                    onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                    placeholder="Team description"
                    className="bg-background border-border"
                    data-testid="team-description-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary text-black font-bold" data-testid="create-team-submit">
                  Create Team
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Teams Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : teams.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-16 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-heading font-semibold text-white mb-2">No Teams Yet</h3>
              <p className="text-muted-foreground mb-6">Create a team to collaborate on deployments</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-primary text-black font-bold">
                <Plus className="w-5 h-5 mr-2" />
                Create Team
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {teams.map((team, i) => (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="bg-card/50 border-border hover:border-primary/30 transition-colors">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center">
                        <Users className="w-6 h-6 text-secondary" />
                      </div>
                      <div>
                        <CardTitle className="text-white font-heading">{team.name}</CardTitle>
                        {team.description && (
                          <p className="text-sm text-muted-foreground">{team.description}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openInviteDialog(team)}
                      className="border-primary text-primary hover:bg-primary/10"
                      data-testid={`invite-btn-${team.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Invite
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <h4 className="text-sm font-medium text-foreground/80 mb-3">
                      Members ({team.members?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {team.members?.map((member) => (
                        <div
                          key={member.user_id}
                          className="flex items-center justify-between p-3 bg-[#0A0E27] rounded-lg border border-border"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-primary">
                                {member.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-white">{member.name}</p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs capitalize">
                              {roleIcons[member.role]}
                              {member.role}
                            </span>
                            {member.role !== 'owner' && team.owner_id === user?.id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveMember(team.id, member.user_id)}
                                className="text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Invite Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="bg-card border-border" aria-describedby="invite-member-description">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">
                Invite to {selectedTeam?.name}
              </DialogTitle>
              <p id="invite-member-description" className="sr-only">Form to invite a member to the team</p>
            </DialogHeader>
            <form onSubmit={handleInviteMember} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-foreground/80">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    placeholder="colleague@company.com"
                    className="pl-10 bg-background border-border"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground/80">Role</Label>
                <Select
                  value={inviteData.role}
                  onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-primary text-black font-bold" data-testid="send-invite-btn">
                Send Invitation
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
